import { fixedUi } from '@/i18n/fixedUi';

/**
 * Tiktok 发品草稿本地存储（按店铺隔离）。
 * 完整 form 走 IndexedDB；列表摘要走 localStorage，便于草稿箱快速展示。
 */

const IDB_NAME = 'ipe-tiktok-publish-drafts';
const IDB_STORE = 'drafts';
const IDB_VERSION = 1;
const INDEX_PREFIX = 'ipe_tiktok_publish_drafts_index_';

function indexKey(shopId) {
  return `${INDEX_PREFIX}${String(shopId || '0')}`;
}

function makeId() {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readIndex(shopId) {
  try {
    const raw = localStorage.getItem(indexKey(shopId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeIndex(shopId, items) {
  try {
    localStorage.setItem(indexKey(shopId), JSON.stringify(items));
  } catch {
    /* quota */
  }
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onerror = () => reject(req.error || new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        const store = db.createObjectStore(IDB_STORE, { keyPath: 'id' });
        store.createIndex('shopId', 'shopId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function idbPut(record) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.oncomplete = () => {
      db.close();
      resolve(record);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('indexedDB put failed'));
    };
    tx.objectStore(IDB_STORE).put(record);
  }));
}

function idbGet(id) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(String(id));
    req.onsuccess = () => {
      db.close();
      resolve(req.result || null);
    };
    req.onerror = () => {
      db.close();
      reject(req.error || new Error('indexedDB get failed'));
    };
  }));
}

function idbDelete(ids) {
  const idList = (Array.isArray(ids) ? ids : [ids]).map(String).filter(Boolean);
  if (!idList.length) return Promise.resolve(0);
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    let deleted = 0;
    tx.oncomplete = () => {
      db.close();
      resolve(deleted);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('indexedDB delete failed'));
    };
    const store = tx.objectStore(IDB_STORE);
    idList.forEach((id) => {
      store.delete(id);
      deleted += 1;
    });
  }));
}

function imageUrlOf(image) {
  if (!image) return '';
  return String(
    image.tiktokUrl
    || image.publicUrl
    || image.ipeUrl
    || image.previewUrl
    || image.url
    || '',
  ).trim();
}

function firstSupplierCode(form) {
  const fromSkc = (form?.skcList || [])
    .map((skc) => String(skc?.supplierCode || '').trim())
    .find(Boolean);
  return fromSkc || String(form?.supplierCode || '').trim();
}

/** 半托管站点 id → 中文名（草稿摘要用） */
const TIKTOK_DRAFT_SITE_LABELS = {
  100: '美国站',
  101: '加拿大站',
  102: '英国站',
  103: '澳大利亚站',
  104: '新西兰站',
  105: '德国站',
  106: '法国站',
  107: '意大利站',
  108: '西班牙站',
  109: '日本站',
  112: '墨西哥站',
  118: '巴西站',
  120: '韩国站',
};

/** 从 form.selectedSites 生成草稿箱「站点」展示文案 */
export function formatTiktokDraftSiteLabel(form = {}) {
  const sites = Array.isArray(form.selectedSites) ? form.selectedSites : [];
  const labels = sites
    .map((raw) => {
      const key = String(raw ?? '').trim();
      if (!key) return '';
      return TIKTOK_DRAFT_SITE_LABELS[key] || TIKTOK_DRAFT_SITE_LABELS[Number(key)] || key;
    })
    .filter(Boolean);
  if (labels.length) return labels.join('、');
  const main = String(form.mainSite || '').trim();
  if (main && main.toLowerCase() !== 'tiktok') return main;
  return '';
}

/** 从 form 生成草稿箱列表摘要 */
export function buildTiktokDraftListSummary(form = {}) {
  const skcItems = (Array.isArray(form.skcList) ? form.skcList : [])
    .filter((skc) => skc && (
      String(skc.colorLabel || '').trim()
      || String(skc.colorValueId || '').trim()
      || imageUrlOf(skc.mainImage)
      || imageUrlOf(skc.colorBlockImage)
    ))
    .map((skc) => ({
      colorLabel: String(skc.colorLabel || skc.colorExtraValue || '').trim() || null,
      skcName: String(skc.skcCode || skc.skcName || '').trim() || null,
      imageUrl: imageUrlOf(skc.mainImage)
        || imageUrlOf(skc.colorBlockImage)
        || null,
      supplierCode: String(skc.supplierCode || '').trim() || null,
    }));

  return {
    title: String(form.title || form.productName || '').trim() || '未命名草稿',
    supplierCode: firstSupplierCode(form) || null,
    categoryLabel: String(
      form.categoryPathLabel
      || form.categoryLabel
      || form.productTypeName
      || '',
    ).trim() || null,
    platformSpuName: String(form.spuName || form.platformSpuName || '').trim() || null,
    siteLabel: formatTiktokDraftSiteLabel(form) || null,
    skcItems,
    source: 'local',
    typeLabel: '新增',
  };
}

function toListItem(meta) {
  return {
    id: meta.id,
    title: meta.title || '未命名草稿',
    supplierCode: meta.supplierCode || null,
    categoryLabel: meta.categoryLabel || null,
    platformSpuName: meta.platformSpuName || null,
    siteLabel: meta.siteLabel || null,
    skcItems: Array.isArray(meta.skcItems) ? meta.skcItems : [],
    source: meta.source || 'local',
    typeLabel: meta.typeLabel || '新增',
    createdAt: meta.createdAt,
    updatedAt: meta.updatedAt,
  };
}

function matchesKeyword(row, keyword) {
  const kw = String(keyword || '').trim().toLowerCase();
  if (!kw) return true;
  const hay = [
    row.title,
    row.supplierCode,
    row.categoryLabel,
    row.platformSpuName,
  ].map((v) => String(v || '').toLowerCase()).join(' ');
  return hay.includes(kw);
}

/**
 * @param {string|number} shopId
 * @param {{ form: object, id?: string }} data
 */
export async function createTiktokLocalPublishDraft(shopId, data = {}) {
  const form = data.form && typeof data.form === 'object' ? data.form : {};
  const now = new Date().toISOString();
  const id = data.id ? String(data.id) : makeId();
  const summary = buildTiktokDraftListSummary(form);
  const record = {
    id,
    shopId: String(shopId || ''),
    form,
    ...summary,
    createdAt: now,
    updatedAt: now,
  };
  try {
    await idbPut(record);
  } catch {
    // IndexedDB 不可用时仍写摘要，详情退化到 localStorage 小表单
    try {
      localStorage.setItem(`ipe_tiktok_publish_draft_body_${id}`, JSON.stringify({ form }));
    } catch {
      throw new Error(fixedUi('本地存储空间不足，请清理后重试'));
    }
  }
  const index = readIndex(shopId).filter((row) => String(row.id) !== id);
  index.unshift(toListItem(record));
  writeIndex(shopId, index);
  return { id, ...summary, createdAt: now, updatedAt: now, ok: true };
}

export async function updateTiktokLocalPublishDraft(shopId, draftId, data = {}) {
  const id = String(draftId || '');
  if (!id) throw new Error(fixedUi('草稿 ID 无效'));
  const form = data.form && typeof data.form === 'object' ? data.form : {};
  const now = new Date().toISOString();
  let createdAt = now;
  try {
    const prev = await idbGet(id);
    if (prev?.createdAt) createdAt = prev.createdAt;
  } catch {
    const fromIndex = readIndex(shopId).find((row) => String(row.id) === id);
    if (fromIndex?.createdAt) createdAt = fromIndex.createdAt;
  }
  const summary = buildTiktokDraftListSummary(form);
  const record = {
    id,
    shopId: String(shopId || ''),
    form,
    ...summary,
    createdAt,
    updatedAt: now,
  };
  try {
    await idbPut(record);
  } catch {
    try {
      localStorage.setItem(`ipe_tiktok_publish_draft_body_${id}`, JSON.stringify({ form }));
    } catch {
      throw new Error(fixedUi('本地存储空间不足，请清理后重试'));
    }
  }
  const index = readIndex(shopId);
  const nextItem = toListItem(record);
  const idx = index.findIndex((row) => String(row.id) === id);
  if (idx >= 0) index[idx] = nextItem;
  else index.unshift(nextItem);
  index.sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
  writeIndex(shopId, index);
  return { id, ...summary, createdAt, updatedAt: now, ok: true };
}

export async function getTiktokLocalPublishDraft(shopId, draftId) {
  const id = String(draftId || '');
  if (!id) return null;
  try {
    const row = await idbGet(id);
    if (row && (!shopId || String(row.shopId) === String(shopId))) {
      return {
        id: row.id,
        form: row.form || {},
        title: row.title,
        supplierCode: row.supplierCode,
        categoryLabel: row.categoryLabel,
        skcItems: row.skcItems,
        createdAt: row.createdAt,
        updatedAt: row.updatedAt,
        source: row.source || 'local',
      };
    }
  } catch {
    /* fallthrough */
  }
  try {
    const raw = localStorage.getItem(`ipe_tiktok_publish_draft_body_${id}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const meta = readIndex(shopId).find((row) => String(row.id) === id) || {};
    return {
      id,
      form: parsed?.form || {},
      ...meta,
      source: 'local',
    };
  } catch {
    return null;
  }
}

export async function listTiktokLocalPublishDrafts(shopId, {
  keyword,
  page = 1,
  pageSize = 20,
} = {}) {
  let items = readIndex(shopId).map(toListItem);
  items = items.filter((row) => matchesKeyword(row, keyword));
  items.sort((a, b) => String(b.updatedAt || b.createdAt || '')
    .localeCompare(String(a.updatedAt || a.createdAt || '')));
  const total = items.length;
  const start = Math.max(0, (Number(page) - 1) * Number(pageSize));
  const slice = items.slice(start, start + Number(pageSize || 20));
  // 用 form 刷新摘要，保证 SKC 列展示货号（兼容旧草稿仅存商家SKU）
  const enriched = await Promise.all(slice.map(async (row) => {
    try {
      const full = await getTiktokLocalPublishDraft(shopId, row.id);
      const form = full?.form;
      if (!form || typeof form !== 'object') return row;
      const fresh = buildTiktokDraftListSummary(form);
      return {
        ...row,
        title: fresh.title || row.title,
        supplierCode: fresh.supplierCode || row.supplierCode,
        categoryLabel: fresh.categoryLabel || row.categoryLabel,
        platformSpuName: fresh.platformSpuName || row.platformSpuName,
        siteLabel: fresh.siteLabel || row.siteLabel || null,
        skcItems: Array.isArray(fresh.skcItems) && fresh.skcItems.length
          ? fresh.skcItems
          : row.skcItems,
      };
    } catch {
      return row;
    }
  }));
  return {
    items: enriched,
    content: enriched,
    total,
    totalElements: total,
    neverSynced: false,
  };
}

export async function deleteTiktokLocalPublishDraft(shopId, draftId) {
  const id = String(draftId || '');
  if (!id) return { ok: true, deleted: 0 };
  try {
    await idbDelete([id]);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(`ipe_tiktok_publish_draft_body_${id}`);
  } catch {
    /* ignore */
  }
  const next = readIndex(shopId).filter((row) => String(row.id) !== id);
  writeIndex(shopId, next);
  return { ok: true, deleted: 1 };
}

export async function batchDeleteTiktokLocalPublishDrafts(shopId, ids = []) {
  const idList = (Array.isArray(ids) ? ids : []).map(String).filter(Boolean);
  if (!idList.length) return { ok: true, deleted: 0 };
  try {
    await idbDelete(idList);
  } catch {
    /* ignore */
  }
  idList.forEach((id) => {
    try {
      localStorage.removeItem(`ipe_tiktok_publish_draft_body_${id}`);
    } catch {
      /* ignore */
    }
  });
  const idSet = new Set(idList);
  const before = readIndex(shopId);
  const next = before.filter((row) => !idSet.has(String(row.id)));
  writeIndex(shopId, next);
  return { ok: true, deleted: before.length - next.length };
}
